import { connect, ConnectedProps } from 'react-redux'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import withLoading from '../../../../../../../../../../utils/hoc/withLoading'
import useQuoteOrderHeaderUrls from '../../../../../../../../../../utils/hooks/quotes/useQuoteOrderHeaderUrls'
import { ComponentTabObject } from '../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../../utils/reducers'
import { CostSellPayload } from '../../../../../../../../../../utils/reducers/company/costsSell'
import QuoteReadOnlyHeaderCosts from '../../../../../header/QuoteReadOnlyHeaderCosts'
import QuoteReadOnlyHeaderFinancialRows from '../../../../../header/QuoteReadOnlyHeaderFinancialRows'
import QuoteStepHeaderMainNoPermission, { useQuoteStepHeaderMainNoPermissionLogic } from '../../../../../header/QuoteStepHeaderMainNoPermission'
import QuoteStepHeaderShippingNoPermission from '../../../../../header/QuoteStepHeaderShippingNoPermission'
import QuoteReadOnlyHeaderAccounts from '../../../../../header/QuoteReadOnlyHeaderAccounts'
import { SellVariablePayload } from '../../../../../../../../../../utils/reducers/company/sellVariable'
import { SellTagsPayload } from '../../../../../../../../../../utils/reducers/sells/sellTags'
import AppStyleDecider from '../../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import ITHeaderText from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { QuoteOrderModelType } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { FormElement, FormRender } from '../../../../../../../../../../forms/FormRenderer/types'
import { displayDate } from '../../../../../../../../../../utils/globals/date'
import { ITHeaderOrderTagsNew } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../utils/globals/tailwind'
import { ProductTypesPayload } from '../../../../../../../../../../utils/reducers/products/productTypes'

type QuoteOrderCreateNoPermissionStepITHeaderProps = InputTableLeftColumnCompleteRequiredProps & ReduxQuoteOrderCreateNoPermissionStepITHeader & {}

const QuoteOrderCreateNoPermissionStepITHeader: React.FC<QuoteOrderCreateNoPermissionStepITHeaderProps> = ({ ...rest }) => {
    return (
        <AppStyleDecider DefaultComponent={QuoteOrderCreateNoPermissionStepITHeaderDefault} NewComponent={QuoteOrderCreateNoPermissionStepITHeaderNew} {...rest} />
    )
}

const QuoteOrderCreateNoPermissionStepITHeaderDefault: React.FC<QuoteOrderCreateNoPermissionStepITHeaderProps> = ({ sellTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useQuoteOrderHeaderUrls()
    const tabs: ComponentTabObject = {
        'main': {
            component: QuoteStepHeaderMainNoPermission,
            text: t('dataQuote', 'Datos del presupuesto'),
            extraProps: { sellTags: sellTags?.allTags }
        },
    }
    if(!rest.headerData?.hidePrices){
        tabs['financialRow'] = {
            component: QuoteReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'quote.order',
            extraProps: { orderHeaderURLs }
        }
        tabs['costs'] = {
            component: QuoteReadOnlyHeaderCosts,
            text: t('costsQuote', 'Gastos del presupuesto'),
            permission: 'quote.order',
            extraProps: { orderHeaderURLs }
        }
    }
    tabs['shipping'] = {
        component: QuoteStepHeaderShippingNoPermission,
        text: t('shipping', 'Transporte'),
        permission: 'quote.order',
        extraProps: {
            orderHeaderURLs
        }
    }
    tabs['accounts'] = {
        component: QuoteReadOnlyHeaderAccounts,
        text: t('divideIntoChapters', 'Dividir en capítulos'),
        permission: 'quote.order',
        extraProps: { orderHeaderURLs }
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const QuoteOrderCreateNoPermissionStepITHeaderNew: React.FC<QuoteOrderCreateNoPermissionStepITHeaderProps> = ({ sellTags, headerData, ...rest }) => {
    const { t } = useTwinTranslation()
    const { memorizedAddresses, customerAddresses, activeCurrencies } = useQuoteStepHeaderMainNoPermissionLogic({ CustomerAddress: headerData?.CustomerAddress || null, WorkingCenterAddress: headerData?.WorkingCenterAddress || null, CustomerId: headerData?.CustomerId })
    let extraFields: FormElement<keyof QuoteOrderModelType>[] = []
    if (headerData?.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} />
            },
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            },
            hideSectionByDefault: true
        })
    } else if (customerAddresses) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={customerAddresses?.[headerData?.CustomerId]} />
            },
            extraProps: {
                customerAddresses
            },
            hideSectionByDefault: true
        },)
    }
    if (headerData?.WorkingCenterAddressId) {
        extraFields.push({
            name: 'WorkingCenterAddressId',
            component: 'Custom',
            render: ({ value, extraProps }) => {
                return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={memorizedAddresses.workingCenterAddress} />
            },
            extraProps: {
                workingCenterAddress: memorizedAddresses.workingCenterAddress
            },
            hideSectionByDefault: true
        },)
    }
    const sections: FormRender<QuoteOrderModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('quoteN', 'Nº presupuesto')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateQuoteOrder', 'Fecha del presupuesto')} value={displayDate(new Date(value))} />
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} />
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (
                            <ITHeaderOrderTagsNew tagType={0} dictTags={sellTags?.allTags || []} className={returnClassOfSpanTemplate(3) + ' order_top_header_custom_select_tags'} selectedTags={extraProps.selectedTags} onChange={async () => { }} headerData={headerData || {}} readOnly={true} />)
                    },
                    extraProps: { selectedTags: headerData?.tags }
                },
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('workingCenterFiscalAddress', 'Dirección Fiscal centro de trabajo')} value={memorizedAddresses?.workingCenterAddress || ''} />
                    },
                    hideSectionByDefault: true
                },
                ...extraFields,
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('workingCenter', 'Centro de trabajo')} value={headerData?.WorkingCenter?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} />
                    },
                    hideSectionByDefault: true
                }
            ]
        }
    ]

    return (
        <InputTableTopHeader handleChange={async () => { }} sections={sections} headerData={headerData} getCompleteData={() => {}} />
    )
}

const QuoteOrderCreateNoPermissionStepITHeaderDispatch = {
    setCostsSell: (payload: CostSellPayload) => ({ type: 'CHANGE_COSTSELL', payload }),
    setSellTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
    setProductTypes: (payload: ProductTypesPayload) => ({ type: 'CHANGE_PRODUCT_TYPES', payload }),
}

export type ReduxQuoteOrderCreateNoPermissionStepITHeader = ConnectedProps<typeof quoteOrderCreateStepHeaderConnect>
const mapQuoteOrderCreateStepHeaderConnector = (state: AllReduxPayloads) => ({ costsSell: state.costsSell, sellTags: state.sellTags, sellVariables: state.sellVariables, productTypes: state.productTypes })
const quoteOrderCreateStepHeaderConnect = connect(mapQuoteOrderCreateStepHeaderConnector, QuoteOrderCreateNoPermissionStepITHeaderDispatch)
const quoteOrderCreateStepHeaderConnectLoading = withLoading(QuoteOrderCreateNoPermissionStepITHeader, [{ fetchUrl: '/api/app/config/cost/costSell/getAllCostSells', propName: 'costsSell', setFunctionName: 'setCostsSell' }, { fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setSellTags' }, { fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }, { fetchUrl: '/api/app/product/productType/getAllProductTypes', propName: 'productTypes', setFunctionName: 'setProductTypes' }])
const QuoteOrderCreateNoPermissionStepITHeaderConnect = quoteOrderCreateStepHeaderConnect(quoteOrderCreateStepHeaderConnectLoading)

export default QuoteOrderCreateNoPermissionStepITHeaderConnect